import Constants from '@/Constants';
import { useEditVideo } from '@/hooks/EditVideo';
import { AvailableLanguage } from '@/models/AvailableLanguage';
import Category from '@/models/Category';
import { EntityType } from '@/models/EntityTypes';
import Item from '@/models/Item';
import { LanguageType } from '@/models/LanguageType';
import { resetNavigationToSetScreen } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getItensClear, postItemClear, postItemRequest, putItemClear, putItemRequest } from '@/store/item/action';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'underscore';
import FamilySelectionBox from '../FamilySelectionBox';
import Modal from '../Modal';
import SelectCategoryForm from '../SelectCategoryForm';
import TagsInput from '../TagsInput';
import Upload from '../Upload';
import { formErrors, formInitialState, schema } from './constants';
import './style.scss';

interface NewArticleProps {
    handleOnCancel: () => void;
    language: LanguageType;
    item?: Item;
}

const NewItem = ({ item, handleOnCancel, language }: NewArticleProps) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const location: string | string[] = history.location.pathname;
    const isEdit = /edit$/g.test(location);
    const [videoUpdate, setVideoUpdate] = useState(false);
    const [{ nameError, authorError, tagsError, pathError }, setFormError] = useState(formErrors);
    const [newFileSelected, setNewFileSelected] = useState<boolean>(false);
    const [formBkpState, setFormBkpState] = useState(formInitialState);
    const [formState, setFormState] = useState(formInitialState);
    const { mainImage, name, description, author, path, category, category2, tags, workload, availableSubtitles, families } = formState;
    const [bkpReady, setBkpReady] = useState<boolean>(false);
    const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
    const { isVideoReady } = useEditVideo(isEdit, newFileSelected, item);
    const [canRedirect, setCanRedirect] = useState<boolean>(false);
    const [canPublish, setCanPublish] = useState<boolean>(false);
    const [categories, setCategories] = useState<Category[]>([]);

    const { success } = useSelector((state: ApplicationState) => state.itens);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);
    const {
        data: { categories: fetchedCategories },
    } = useSelector((state: ApplicationState) => state.categories);

    const subtitlePath: string = availableSubtitles?.find((subtitle: AvailableLanguage) => subtitle.locale === language)?.path || '';

    useEffect(() => {
        resetNavigationToSetScreen(EntityType.CONTENT, item?.organizationId, identity.user.admin, history);

        return () => {
            setFormState(formInitialState);
            dispatch(getItensClear());
        };
    }, [item, identity?.user.admin]);

    useEffect(() => {
        if (canRedirect) {
            history.push('/contents/playlist/items');
        }
    }, [canRedirect]);

    useEffect(() => {
        if (success && !item) {
            setFormState(formInitialState);
            dispatch(postItemClear());
            setCanRedirect(true);
        } else if (success && item) {
            dispatch(putItemClear());
            setCanRedirect(true);
        }
    }, [item, success]);

    const handleOnChange = useCallback(
        (fieldName: string, value: any) => {
            if (fieldName === 'path') {
                setVideoUpdate(true);
            }

            setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
        },
        [fetchedCategories],
    );

    useEffect(() => {
        if (item && !bkpReady) {
            handleOnChange('mainImage', item.mainImage);
            handleOnChange('author', item.author);
            handleOnChange('path', item.path);
            handleOnChange('subtitle', item.subtitle);
            handleOnChange('name', item.name);
            handleOnChange('description', item.description);
            handleOnChange('category', item.categories && item.categories.length > 0 ? item.categories[0] : undefined);
            handleOnChange('category2', item.categories && item.categories.length > 1 ? item.categories[1] : undefined);
            handleOnChange('tags', item.tags && item.tags.length > 0 ? item.tags : []);
            handleOnChange('workload', item.workload);
            handleOnChange('availableSubtitles', item.availableSubtitles);
            setVideoUpdate(false);
            handleOnChange('families', item.families && item.families.length > 0 ? item.families : []);
            setBkpReady(true);
        }
    }, [handleOnChange, item]);

    const handlePublish = async () => {
        await handleSubmit(true);
    };

    const handleSubmit = async (willPublish: boolean = false) => {
        setCanPublish(false);
        try {
            setFormError(formErrors);

            await schema.validate(
                {
                    name,
                    path,
                },
                { abortEarly: false },
            );

            const ITEM: Item = {
                '@class': '.MediaContent',
                prerequisites: true,
                mandatory: true,
                gradeWeight: 1,
                masteryScore: null,
                type: 'MediaContent',
                visibility: false,
                totalRating: 0,
                status: item ? item.status : 'DRAFT',
                identifier: `content_${new Date().getTime().toString()}`,

                applicationId: Constants.APP_ID,
                organizationId: families.length > 0 ? null : identity.user.orgId,

                mediaType: 'VIDEO',
                locale: language,
                description,
                mainImage,
                path,
                name,
                author,
                tags,
                categories,
                workload,
                moduleId: Constants.PLAYLIST_MODULE_ID,
                availableSubtitles,
                families,
            };

            if (item) {
                dispatch(
                    putItemRequest({
                        item: {
                            ...item,
                            name: ITEM.name,
                            path: ITEM.path,
                            type: ITEM.type,
                            author: ITEM.author,
                            workload: ITEM.workload,
                            categories: ITEM.categories,
                            tags: ITEM.tags,
                            locale: language,
                            mainImage,
                            description: ITEM.description,
                            availableSubtitles: ITEM.availableSubtitles,
                            families: ITEM.families,
                        },
                        videoUpdate,
                    }),
                );
            } else {
                dispatch(postItemRequest({ item: ITEM, willPublish }));
            }
        } catch (err) {
            if (err.inner) {
                err.inner.map((error: any) =>
                    setFormError((prevState) => ({
                        ...prevState,
                        [`${error.path}Error`]: true,
                    })),
                );
            } else {
                console.error(err);
            }
        }
    };
    useEffect(() => {
        const initialForm: boolean = _.isEqual(formBkpState, formInitialState);

        if (!initialForm && bkpReady) {
            setSaveDisabled(_.isEqual(formState, formBkpState));
        } else {
            setFormBkpState(formState);
        }

        setCanPublish(Boolean(mainImage && name && author && path && category?.id && tags.length > 0));
    }, [formState]);

    useEffect(() => {
        const addingAnotherLanguage: boolean = language !== (item && item.locale);
        if (bkpReady) {
            setFormBkpState(formInitialState);
            if (addingAnotherLanguage) {
                setBkpReady(false);
                setSaveDisabled(false);
            } else {
                setSaveDisabled(true);
            }
        }
    }, [language]);

    useEffect(() => {
        const updatedCategories: Category[] = [];

        if (category?.id) {
            updatedCategories.push(category);
        }

        if (category2?.id) {
            updatedCategories.push(category2);
        }

        setCategories(updatedCategories);
    }, [category, category2]);

    return (
        <>
            <Modal
                content={strings.LEAVE_SECTION_MESSAGE}
                handleLeftClick={handleOnCancel}
                icon="announcement"
                leftButtonText={strings.CONFIRM_EXIT}
                rightButtonText={strings.NO}
                title={strings.ATTENTION}
            />

            <div className="form-group new-item">
                <div className="ml-5 mr-5 mt-4">
                    <FamilySelectionBox disabled={!!item?.id} familyId={families[0]?.id} handleOnChange={handleOnChange} />
                </div>

                <div className="input-section">
                    <h1>
                        {strings.HEADER} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                    </h1>
                    <div className="ml-5 mr-5">
                        <Upload
                            onComplete={(e) => handleOnChange('mainImage', e.key)}
                            onRemove={() => handleOnChange('mainImage', undefined)}
                            previewFile={{ uri: mainImage, name: mainImage }}
                            typeAccepted="IMAGE"
                        />
                    </div>
                </div>

                <div className="row input-section">
                    <div className="title-container">
                        <h1>
                            {strings.TITLE} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        {nameError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                    </div>
                    <input
                        className={`form-control ${nameError ? 'error' : ''}`}
                        id="titleInput"
                        onChange={(e) => handleOnChange('name', e.target.value)}
                        placeholder={strings.TYPE_TITLE}
                        required
                        type="text"
                        value={name || ''}
                    />
                </div>

                <div className="row input-section">
                    <div className="title-container">
                        <h1>{strings.ABOUT} </h1>
                    </div>
                    <textarea
                        className="form-control"
                        id="aboutInput"
                        onChange={(e) => handleOnChange('description', e.target.value)}
                        placeholder={strings.ABOUT_INSERT}
                        required
                        rows={3}
                        value={description || ''}
                    />
                </div>

                <div className="row input-section">
                    <div className="title-container">
                        <h1>
                            {strings.AUTHOR} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        {authorError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                    </div>
                    <input
                        className={`form-control ${authorError ? 'error' : ''}`}
                        id="titleInput"
                        onChange={(e) => handleOnChange('author', e.target.value)}
                        placeholder={strings.TYPE_AUTHOR}
                        required
                        type="text"
                        value={author || ''}
                    />
                </div>

                <div className="input-section">
                    <div className="title-container">
                        <h1>
                            {strings.VIDEO_FILE} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                        </h1>
                        {pathError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                    </div>
                    <div className="ml-5 mr-5">
                        <Upload
                            fileOnQueueToConvert={isVideoReady}
                            onComplete={(e) => {
                                setNewFileSelected(true);
                                handleOnChange('path', e.key);
                            }}
                            onLoadedMetadata={(minutes: number) => handleOnChange('workload', minutes)}
                            onRemove={() => handleOnChange('path', undefined)}
                            previewFile={{ uri: path, name: path }}
                            typeAccepted="VIDEO"
                        />
                    </div>
                </div>

                <div className="input-section">
                    <h1>{strings.HAS_SUBTITLES}</h1>
                    <div className="ml-5 mr-5">
                        <Upload
                            onComplete={(e) => handleOnChange('availableSubtitles', [{ path: e.key, locale: language }])}
                            onRemove={() => handleOnChange('availableSubtitles', undefined)}
                            previewFile={{ uri: subtitlePath, name: subtitlePath }}
                            typeAccepted="SUBTITLE"
                        />
                    </div>
                </div>

                <div className="ml-5 mr-5 mt-4 required">
                    <SelectCategoryForm
                        categoriesForm={categories}
                        familyId={families[0]?.id}
                        handleOnChange={(fieldName, categories) => {
                            const [category, category2] = categories || [];
                            handleOnChange('category', category);
                            handleOnChange('category2', category2);
                        }}
                        isColumnFormat
                    />
                </div>

                <div className="row input-section">
                    <div className="d-flex flex-fill flex-column">
                        <div className="title-container">
                            <h1>
                                {strings.TAGS} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                            </h1>
                            {tagsError && <h1 className="error-message">{strings.ERROR_MESSAGE}</h1>}
                        </div>
                        <TagsInput hasError={tagsError} selectedTags={tags} setTags={handleOnChange} />
                    </div>
                </div>
                <div className="warning-input">
                    <p className="warning-text">
                        {Constants.symbol.ASTERISK}
                        {strings.REQUIRED_FIELDS}
                    </p>
                </div>
                <div className="d-flex vertical-center justify-content-end h-100 w-100 submit-buttons">
                    <button className="btn btn-link" data-target="#alert-modal" data-toggle="modal" type="button">
                        {strings.CANCEL}
                    </button>
                    {!isEdit && (
                        <>
                            <button className="btn btn-primary btn-sm save-close" disabled={!canPublish} onClick={() => handleSubmit()} type="button">
                                {strings.SAVE_AND_CLOSE}
                            </button>
                            {(!item || (item && item.status !== 'PUBLISHED')) && (
                                <button className="btn btn-primary btn-sm" disabled={!canPublish} onClick={handlePublish} type="button">
                                    {strings.PUBLISH}
                                </button>
                            )}
                        </>
                    )}
                    {isEdit && (
                        <>
                            <button
                                className="btn btn-primary btn-sm save-close"
                                disabled={saveDisabled || !canPublish}
                                onClick={() => handleSubmit()}
                                type="button"
                            >
                                {strings.SAVE_AND_CLOSE}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default NewItem;
